import React, { useEffect } from "react";
import { Form, Input, Switch, DatePicker, Row, Col, Button } from "antd";
import dayjs from "dayjs";
import { CUSTOMER_FORM_FIELDS, CUSTOMER_FORM_SECTIONS } from "./formFields";

const DATE_FORMAT = "YYYY-MM-DD";
const DATETIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

const toDayjs = (value) => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const toFormValues = (customer) => {
  const values = {};

  CUSTOMER_FORM_FIELDS.forEach((field) => {
    const raw = customer?.[field.key];

    if (field.type === "flag") {
      values[field.key] = Number(raw) === 1;
      return;
    }

    if (field.type === "date") {
      values[field.key] = toDayjs(raw);
      return;
    }

    if (field.type === "datetime") {
      values[field.key] = toDayjs(raw);
      return;
    }

    if (field.type === "number" || field.type === "decimal") {
      values[field.key] =
        raw === null || raw === undefined || raw === "" ? "" : String(raw);
      return;
    }

    values[field.key] = raw ?? "";
  });

  return values;
};

export const toApiPayload = (formValues) => {
  const payload = {};

  CUSTOMER_FORM_FIELDS.forEach((field) => {
    if (field.readOnly) return;

    const value = formValues?.[field.key];

    if (field.type === "flag") {
      payload[field.key] = value ? 1 : 0;
      return;
    }

    if (field.type === "date") {
      payload[field.key] = value ? dayjs(value).format(DATE_FORMAT) : null;
      return;
    }

    if (field.type === "datetime") {
      payload[field.key] = value ? dayjs(value).format(DATETIME_FORMAT) : null;
      return;
    }

    if (field.type === "number" || field.type === "decimal") {
      if (value === null || value === undefined || value === "") {
        payload[field.key] = null;
        return;
      }
      payload[field.key] = Number(value);
      return;
    }

    payload[field.key] = value === undefined || value === "" ? null : value;
  });

  return payload;
};

const FieldLabel = ({ field }) => (
  <span className="Customers-field-label">
    {field.label}
    {field.type !== "flag" && (
      <span className="Customers-field-sample">Sample: {field.sample}</span>
    )}
  </span>
);

const getColSpan = () => ({ xs: 24, sm: 12, lg: 8 });

const isEmptyValue = (value) =>
  value === null || value === undefined || value === "";

const getFieldRules = (field) => {
  if (field.type === "text") {
    return [
      {
        validator: (_, value) => {
          if (isEmptyValue(value)) return Promise.resolve();
          const text = String(value).trim();
          if (/\d/.test(text)) {
            return Promise.reject(
              new Error("Enter text only (numbers are not allowed)")
            );
          }
          if (!/^[A-Za-z\s\-_.]+$/.test(text)) {
            return Promise.reject(
              new Error("Enter letters only (e.g. UK, Gold)")
            );
          }
          return Promise.resolve();
        },
      },
    ];
  }

  if (field.type === "number") {
    return [
      {
        validator: (_, value) => {
          if (isEmptyValue(value)) return Promise.resolve();
          const text = String(value).trim();
          if (!/^-?\d+$/.test(text)) {
            return Promise.reject(new Error("Enter a whole number (e.g. 34)"));
          }
          const num = Number(text);
          if (field.key === "age" && (num < 0 || num > 150)) {
            return Promise.reject(new Error("Age must be between 0 and 150"));
          }
          return Promise.resolve();
        },
      },
    ];
  }

  if (field.type === "decimal") {
    return [
      {
        validator: (_, value) => {
          if (isEmptyValue(value)) return Promise.resolve();
          const text = String(value).trim();
          if (!/^-?\d+(\.\d+)?$/.test(text)) {
            return Promise.reject(
              new Error("Enter a valid decimal number (e.g. 1250.50)")
            );
          }
          return Promise.resolve();
        },
      },
    ];
  }

  return [];
};

const calcAgeFromDob = (dob) => {
  if (!dob || !dayjs(dob).isValid()) return null;
  return dayjs().diff(dayjs(dob), "year");
};

const applyDobRelativeTo = (allValues, birthdayDate) => {
  const target = dayjs(birthdayDate).startOf("day");
  let age = allValues.age;

  if (age === null || age === undefined || age === "") {
    age = calcAgeFromDob(allValues.date_of_birth);
  }

  age = age === null || age === undefined || age === "" ? 0 : Number(age);

  const dateOfBirth = target.subtract(age, "year");

  return {
    date_of_birth: dateOfBirth,
    age: calcAgeFromDob(dateOfBirth),
  };
};

const applyDobIsToday = (allValues) =>
  applyDobRelativeTo(allValues, dayjs());

const applyDobIn7Days = (allValues) =>
  applyDobRelativeTo(allValues, dayjs().add(7, "day"));

const renderFieldInput = (field, { dobLocked }) => {
  const lockDobFields =
    dobLocked && (field.key === "age" || field.key === "date_of_birth");

  switch (field.type) {
    case "flag":
      return <Switch checkedChildren="Yes" unCheckedChildren="No" />;
    case "number":
    case "decimal":
      return (
        <Input
          className="Customers-input"
          disabled={field.readOnly || lockDobFields}
          inputMode={field.type === "decimal" ? "decimal" : "numeric"}
        />
      );
    case "date":
      return (
        <DatePicker
          className="Customers-input"
          style={{ width: "100%" }}
          format={DATE_FORMAT}
          disabled={lockDobFields}
        />
      );
    case "datetime":
      return (
        <DatePicker
          showTime
          className="Customers-input"
          style={{ width: "100%" }}
          format={DATETIME_FORMAT}
        />
      );
    default:
      return (
        <Input
          className="Customers-input"
          disabled={field.readOnly}
        />
      );
  }
};

const CustomerForm = ({ customer, onUpdate, updating }) => {
  const [form] = Form.useForm();
  const dobIsToday = Form.useWatch("dob_is_today", form);
  const dobIn7Days = Form.useWatch("dob_is_in_7_days", form);
  const dobLocked = !!dobIsToday || !!dobIn7Days;

  useEffect(() => {
    if (customer) {
      const values = toFormValues(customer);
      if (values.dob_is_today) {
        Object.assign(values, applyDobIsToday(values));
        values.dob_is_in_7_days = false;
      } else if (values.dob_is_in_7_days) {
        Object.assign(values, applyDobIn7Days(values));
      }
      form.setFieldsValue(values);
    } else {
      form.resetFields();
    }
  }, [customer, form]);

  const handleValuesChange = (changedValues, allValues) => {
    if (Object.prototype.hasOwnProperty.call(changedValues, "dob_is_today")) {
      if (changedValues.dob_is_today) {
        form.setFieldsValue({
          ...applyDobIsToday(allValues),
          dob_is_in_7_days: false,
        });
      }
      return;
    }

    if (Object.prototype.hasOwnProperty.call(changedValues, "dob_is_in_7_days")) {
      if (changedValues.dob_is_in_7_days) {
        form.setFieldsValue({
          ...applyDobIn7Days(allValues),
          dob_is_today: false,
        });
      }
      return;
    }

    // When either DOB flag is on, age/DOB stay locked and driven by that toggle.
    if (allValues.dob_is_today || allValues.dob_is_in_7_days) return;

    if (Object.prototype.hasOwnProperty.call(changedValues, "date_of_birth")) {
      form.setFieldsValue({
        age: calcAgeFromDob(changedValues.date_of_birth),
      });
      return;
    }

    if (Object.prototype.hasOwnProperty.call(changedValues, "age")) {
      const years = Number(changedValues.age);
      if (Number.isNaN(years) || years < 0 || !/^-?\d+$/.test(String(changedValues.age).trim())) {
        return;
      }

      const currentDob = allValues.date_of_birth;
      const base =
        currentDob && dayjs(currentDob).isValid() ? dayjs(currentDob) : dayjs();

      form.setFieldsValue({
        date_of_birth: base.year(dayjs().year() - years),
      });
    }
  };

  const handleUpdate = async () => {
    try {
      const values = await form.validateFields();
      if (onUpdate) {
        await onUpdate(toApiPayload(values));
      }
    } catch (error) {
      // Field-level validation messages are already shown under each input.
    }
  };

  if (!customer) {
    return null;
  }

  return (
    <div className="Customers-form-wrap">
      <div className="Customers-form-header">
        <div>
          <h3 className="Customers-form-title">Customer Details</h3>
          <p className="Customers-form-subtitle">
            {customer.email || "Customer profile"}
            {customer.id != null ? ` · ID ${customer.id}` : ""}
          </p>
        </div>
        <Button
          type="primary"
          className="Customers-update-btn"
          onClick={handleUpdate}
          loading={updating}
        >
          Update Customer
        </Button>
      </div>

      <Form
        form={form}
        layout="vertical"
        className="Customers-form"
        requiredMark={false}
        onValuesChange={handleValuesChange}
      >
        {CUSTOMER_FORM_SECTIONS.map((section) => (
          <section key={section.key} className="Customers-section">
            <div className="Customers-section-head">
              <h4 className="Customers-section-title">{section.title}</h4>
              <p className="Customers-section-desc">{section.description}</p>
            </div>

            <Row gutter={[20, 4]}>
              {section.fields.map((field) => (
                <Col {...getColSpan()} key={field.key}>
                  <Form.Item
                    className={
                      field.type === "flag"
                        ? "Customers-form-item Customers-form-item-flag"
                        : "Customers-form-item"
                    }
                    label={<FieldLabel field={field} />}
                    name={field.key}
                    valuePropName={field.type === "flag" ? "checked" : "value"}
                    rules={getFieldRules(field)}
                    validateTrigger={["onChange", "onBlur"]}
                  >
                    {renderFieldInput(field, { dobLocked })}
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </section>
        ))}
      
        <Button
          type="primary"
          className="Customers-update-btn"
          onClick={handleUpdate}
          loading={updating}
        >
          Update Customer
        </Button>
     
      </Form>
    </div>
  );
};

export default CustomerForm;
